import type { Announcement, TemplateType, RelationshipType } from '@/types/announcement';

export interface TreeFruit {
  id: string;
  recipientName: string;
  relationship: RelationshipType;
  template: TemplateType;
  message: string;
  createdAt: string;
  publishedAt: string | null;
  publicSlug: string | null;
  status: string;
}

export interface BranchData {
  recipientName: string;
  relationship: RelationshipType | null;
  fruits: TreeFruit[];
  lastSentAt: string | null;
}

// Fruit type → visual mapping
export const FRUIT_VISUAL: Record<TemplateType, { emoji: string; color: string; label: string }> = {
  love: { emoji: '🍑', color: '#ffb3a7', label: 'Peach' },
  gratitude: { emoji: '🍊', color: '#ffa94d', label: 'Orange' },
  apology: { emoji: '🍎', color: '#e03131', label: 'Apple' },
  closure: { emoji: '🍇', color: '#9775fa', label: 'Grapes' },
  hard_conversation: { emoji: '🍐', color: '#94d82d', label: 'Pear' },
  custom: { emoji: '🍒', color: '#f06595', label: 'Cherry' },
  birthday: { emoji: '🎂', color: '#f59e0b', label: 'Cake' },
  anniversary: { emoji: '💎', color: '#60a5fa', label: 'Diamond' },
  celebration: { emoji: '🎉', color: '#a855f7', label: 'Party' },
  encouragement: { emoji: '🌟', color: '#10b981', label: 'Star' },
};

// Branch anchor positions (% of container) for placing fruits
// These map to visible branch areas on the reference image
export const BRANCH_ANCHORS: { x: number; y: number; angle: number }[] = [
  // Left upper branches
  { x: 15, y: 18, angle: -30 },
  { x: 22, y: 12, angle: -20 },
  { x: 10, y: 28, angle: -40 },
  { x: 28, y: 22, angle: -15 },
  // Left mid branches
  { x: 18, y: 38, angle: -25 },
  { x: 25, y: 32, angle: -10 },
  { x: 12, y: 45, angle: -35 },
  // Center upper
  { x: 42, y: 10, angle: 5 },
  { x: 50, y: 8, angle: 0 },
  { x: 58, y: 10, angle: -5 },
  { x: 48, y: 18, angle: 3 },
  // Right upper branches
  { x: 72, y: 15, angle: 20 },
  { x: 78, y: 12, angle: 25 },
  { x: 85, y: 20, angle: 35 },
  { x: 68, y: 22, angle: 15 },
  // Right mid branches
  { x: 80, y: 30, angle: 30 },
  { x: 75, y: 38, angle: 20 },
  { x: 88, y: 35, angle: 40 },
  // Lower branches
  { x: 30, y: 42, angle: -8 },
  { x: 70, y: 42, angle: 8 },
  { x: 35, y: 48, angle: -5 },
  { x: 65, y: 48, angle: 5 },
  // Hanging positions (grapes/clusters)
  { x: 20, y: 52, angle: -15 },
  { x: 35, y: 55, angle: -5 },
  { x: 50, y: 50, angle: 0 },
  { x: 65, y: 55, angle: 5 },
  { x: 80, y: 52, angle: 15 },
];

export function announcementToFruit(a: Announcement): TreeFruit {
  return {
    id: a.id,
    recipientName: a.recipient_name,
    relationship: a.relationship,
    template: a.template,
    message: a.message,
    createdAt: a.created_at,
    publishedAt: a.published_at,
    publicSlug: a.public_slug,
    status: a.status,
  };
}

export function groupByRecipient(fruits: TreeFruit[]): BranchData[] {
  const map = new Map<string, BranchData>();
  for (const f of fruits) {
    const key = f.recipientName.toLowerCase().trim();
    if (!map.has(key)) {
      map.set(key, {
        recipientName: f.recipientName,
        relationship: f.relationship,
        fruits: [],
        lastSentAt: null,
      });
    }
    const branch = map.get(key)!;
    branch.fruits.push(f);
    if (!branch.lastSentAt || f.createdAt > branch.lastSentAt) {
      branch.lastSentAt = f.createdAt;
    }
  }
  return Array.from(map.values()).sort((a, b) => b.fruits.length - a.fruits.length);
}

/** Days since a date string */
export function daysSince(dateStr: string | null): number {
  if (!dateStr) return Infinity;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

/** Get fruit ripeness stage */
export function getRipenessStage(createdAt: string): 'new' | 'growing' | 'ripe' | 'golden' {
  const days = daysSince(createdAt);
  if (days <= 3) return 'new';
  if (days <= 7) return 'growing';
  if (days <= 30) return 'ripe';
  return 'golden';
}

/** Get branch health based on last interaction */
export function getBranchHealth(lastSentAt: string | null): 'active' | 'warm' | 'cool' | 'dormant' {
  const days = daysSince(lastSentAt);
  if (days <= 7) return 'active';
  if (days <= 14) return 'warm';
  if (days <= 30) return 'cool';
  return 'dormant';
}
